import { useEffect, useMemo, useState } from "react";
import { WagmiProvider, createConfig, http } from "wagmi";
import {
  coinbaseWallet,
  metaMask,
  walletConnect,
  injected,
} from "wagmi/connectors";
import { mainnet } from "wagmi/chains";

import { constants } from "config";

type UsercentricsService = {
  name?: string;
  id?: string;
  consent?: {
    status?: boolean;
  };
};

type UsercentricsServicesBaseInfo =
  | UsercentricsService[]
  | Record<string, UsercentricsService>
  | undefined;

declare global {
  interface Window {
    UC_UI?: {
      getServicesBaseInfo?: () => UsercentricsServicesBaseInfo;
    };
  }
}

const walletConnectServiceNames = ["walletconnect", "wallet connect"];

function getUsercentricsServices() {
  const servicesBaseInfo = window.UC_UI?.getServicesBaseInfo?.();

  if (!servicesBaseInfo) {
    return [];
  }

  if (Array.isArray(servicesBaseInfo)) {
    return servicesBaseInfo;
  }

  return Object.values(servicesBaseInfo);
}

function hasWalletConnectConsent() {
  return getUsercentricsServices().some((service) => {
    const serviceName = service.name?.toLowerCase();
    const serviceId = service.id?.toLowerCase();
    const isWalletConnectService = walletConnectServiceNames.some(
      (walletConnectServiceName) =>
        serviceName?.includes(walletConnectServiceName) ||
        serviceId?.includes(walletConnectServiceName),
    );

    return isWalletConnectService && service.consent?.status === true;
  });
}

function createWalletConfig(isWalletConnectAllowed: boolean) {
  return createConfig({
    chains: [mainnet],
    multiInjectedProviderDiscovery: false,
    connectors: [
      metaMask({ enableAnalytics: false }),
      injected({
        target() {
          return {
            name: "OKX Wallet",
            id: "okxWallet",
            provider: window.okxwallet,
          };
        },
      }),
      coinbaseWallet({ appName: "HUG.art" }),
      ...(isWalletConnectAllowed
        ? [
            walletConnect({
              projectId: constants.walletConnectProjectId,
              showQrModal: true,
              telemetryEnabled: false,
            }),
          ]
        : []),
    ],
    transports: {
      [mainnet.id]: http(
        `https://eth-mainnet.g.alchemy.com/v2/${constants.alchemyApiKey}`,
      ),
    },
  });
}

function WalletProvider({ children }: { children: React.ReactNode }) {
  const [isWalletConnectAllowed, setIsWalletConnectAllowed] = useState(
    hasWalletConnectConsent,
  );
  const config = useMemo(
    () => createWalletConfig(isWalletConnectAllowed),
    [isWalletConnectAllowed],
  );

  useEffect(() => {
    function updateWalletConnectConsent() {
      setIsWalletConnectAllowed(hasWalletConnectConsent());
    }

    window.addEventListener("UC_UI_INITIALIZED", updateWalletConnectConsent);
    window.addEventListener("UC_UI_CMP_EVENT", updateWalletConnectConsent);
    window.addEventListener("UC_UI_VIEW_CHANGED", updateWalletConnectConsent);
    updateWalletConnectConsent();

    return () => {
      window.removeEventListener(
        "UC_UI_INITIALIZED",
        updateWalletConnectConsent,
      );
      window.removeEventListener("UC_UI_CMP_EVENT", updateWalletConnectConsent);
      window.removeEventListener(
        "UC_UI_VIEW_CHANGED",
        updateWalletConnectConsent,
      );
    };
  }, []);

  return <WagmiProvider config={config}>{children}</WagmiProvider>;
}

export { WalletProvider };
